import { useCallback, useEffect, useRef, useState } from "react";
import {
	DEVICE_ONLY_INSTANCE_SIZES,
	ENVIRONMENT_INSTANCE_SIZES,
	getEnvironmentInstanceSizes,
	isGpuInstanceSize,
	type EnvironmentInstanceSize,
	type EnvironmentInstanceSizeInfo,
	type EnvironmentRunner,
} from "@/lib/api";
import { formatCredits } from "@/lib/billing";
import {
	formatCpuRange,
	formatGib,
	formatMemory,
	parseCpu,
	parseMemoryBytes,
} from "@/lib/format-k8s-quantity";
import { ConfirmButton } from "@/components/common/ConfirmButton";

/** 「起動」で確定した選択。gpu はデバイスの環境だけ持つ。 */
export interface EnvironmentStartChoice {
	size: EnvironmentInstanceSize;
	gpu?: boolean;
}

interface EnvironmentInstanceSizeDialogProps {
	open: boolean;
	/** 初期選択。前回起動したサイズ (環境に保存済み) を渡す。 */
	initialSize: EnvironmentInstanceSize;
	/** デバイスの環境の「GPU を使う」の初期値 (環境に保存済みの値)。 */
	initialGpu?: boolean;
	onClose: () => void;
	/** 「起動」で確定した選択。ダイアログを閉じるのは呼び出し側の責務。 */
	onConfirm: (choice: EnvironmentStartChoice) => void;
	/**
	 * デバイスの環境なら、そのデバイス。GPU のサイズの代わりに「GPU を使う」を出し、GPU を報告していなければ選べなくする。
	 * デバイスの CPU 数・メモリの総量を超えるサイズも選べなくする。
	 */
	runner?: EnvironmentRunner | null;
}

const GENERAL_INSTANCE_SIZE_COUNT = ENVIRONMENT_INSTANCE_SIZES.filter(
	(size) => !isGpuInstanceSize(size),
).length;

/** 「GPU を使う」を選べない理由。backend が理由のコードから作った文を出し、無ければ汎用の文にする。 */
function noGpuReason(runner: EnvironmentRunner): string {
	return runner.gpuMessage ?? "このデバイスでは GPU を使えません。";
}

/**
 * サイズの CPU・メモリの上限がデバイスの総量を超えていれば理由を返す。報告の無い値は判定しない。
 * backend の RunnerStartGuidance.SizeTooLarge と同じ判定。
 */
function sizeTooLargeReason(
	info: EnvironmentInstanceSizeInfo,
	runner: EnvironmentRunner,
): string | null {
	const { hostCpus, hostMemoryBytes } = runner;
	const shortages = [
		hostCpus !== null && parseCpu(info.cpuLimit) > hostCpus ? ` CPU (${hostCpus} vCPU)` : null,
		hostMemoryBytes !== null && parseMemoryBytes(info.memory) > hostMemoryBytes
			? `メモリ (${formatGib(hostMemoryBytes)} GiB)`
			: null,
	].filter((shortage) => shortage !== null);
	return shortages.length > 0 ? `このデバイスの${shortages.join("・")} では起動できません` : null;
}

/**
 * 環境の起動時にインスタンスサイズを選ぶモーダルダイアログ。GPU の無いサイズ (S〜XXXL、デバイスでは 4XL・5XL も) と
 * GPU サイズを節に分けて並べ、各サイズの CPU・メモリ・GPU と稼働 1 時間あたりの
 * クレジットをサーバーから取得して見せ、「起動」で確定する。
 * 選んだサイズは環境に保存され、次回の起動と再起動はこのダイアログを経ずに同じサイズを使う。
 * デバイスの環境では汎用のサイズだけを並べ (CPU・メモリはサイズの上限で動く)、その下の「GPU を使う」でデバイスのすべての GPU を
 * 渡すかを選ぶ (既定は保存済みの値。GPU を報告していないデバイスでは選べず、保存済みの値を変えない)。デバイスの CPU 数・メモリの総量を
 * 超えるサイズは選べず、保存済みのサイズがそれに当たれば、選び直すまで起動を確定できない。稼働は課金しないので単価は出さない。
 */
export function EnvironmentInstanceSizeDialog({
	open,
	initialSize,
	initialGpu = false,
	onClose,
	onConfirm,
	runner = null,
}: EnvironmentInstanceSizeDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [sizes, setSizes] = useState<EnvironmentInstanceSizeInfo[] | null>(null);
	const runnerHasGpu = runner?.hasGpu ?? false;
	const [selected, setSelected] = useState<EnvironmentInstanceSize>(initialSize);
	const [gpuChecked, setGpuChecked] = useState(initialGpu && runnerHasGpu);
	const [loadError, setLoadError] = useState<string | null>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	useEffect(() => {
		if (!open) return;
		setSelected(initialSize);
		// 開き直したときは前回の一覧を出したまま差し替える。空に戻すと一覧が一瞬消えてちらつく。
		setLoadError(null);
		const controller = new AbortController();
		getEnvironmentInstanceSizes(controller.signal)
			.then(setSizes)
			.catch((err: unknown) => {
				if (controller.signal.aborted) return;
				setLoadError(
					err instanceof Error ? err.message : "インスタンスサイズの取得に失敗しました。",
				);
			});
		return () => controller.abort();
	}, [open, initialSize]);

	useEffect(() => {
		if (open) setGpuChecked(initialGpu && runnerHasGpu);
	}, [open, initialGpu, runnerHasGpu]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				e.preventDefault();
			}
		},
		[onClose],
	);

	if (!open) return null;

	const shared =
		sizes?.filter((info) => info.gpu === null && (runner !== null || !info.deviceOnly)) ?? [];
	const gpu = runner ? [] : (sizes?.filter((info) => info.gpu !== null) ?? []);
	const gpuDisabledReason = runner !== null && !runner.hasGpu ? noGpuReason(runner) : null;
	const sizeDisabledReason = (info: EnvironmentInstanceSizeInfo) =>
		runner ? sizeTooLargeReason(info, runner) : null;
	const selectedInfo = shared.find((info) => info.size === selected);
	const selectedUnavailable =
		runner !== null &&
		(isGpuInstanceSize(selected) ||
			(selectedInfo !== undefined && sizeDisabledReason(selectedInfo) !== null));
	// デバイスでは汎用のサイズ (4XL・5XL を含む) と「GPU を使う」、クラウドではデバイス専用を除くサイズの行が並ぶ。
	const placeholderCount = runner
		? GENERAL_INSTANCE_SIZE_COUNT + 1
		: ENVIRONMENT_INSTANCE_SIZES.length - DEVICE_ONLY_INSTANCE_SIZES.length;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			data-testid="environment-instance-size-dialog"
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width: 560,
				maxWidth: "calc(100vw - 32px)",
			}}
			className="backdrop:bg-scrim"
		>
			<form
				noValidate
				onSubmit={(e) => {
					e.preventDefault();
					if (!sizes) return;
					// 「GPU を使う」を選べないときは gpu を送らず、保存済みの値 (未選択なら null) を保つ。
					// ただし GPU ありで保存した環境は、外して送らないと起動を断られ続ける。
					if (runnerHasGpu) onConfirm({ size: selected, gpu: gpuChecked });
					else if (runner && initialGpu) onConfirm({ size: selected, gpu: false });
					else onConfirm({ size: selected });
				}}
			>
				<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
					<h3
						style={{
							margin: 0,
							fontSize: "var(--text-title)",
							fontWeight: 600,
							color: "var(--fg-1)",
							fontFamily: "var(--font-sans)",
						}}
					>
						インスタンスサイズを選択
					</h3>
					<p
						style={{
							margin: "6px 0 0",
							fontSize: "var(--text-caption)",
							lineHeight: "var(--leading-body)",
							color: "var(--fg-2)",
							fontFamily: "var(--font-sans)",
						}}
					>
						{runner
							? `再起動は同じサイズを使い、次回の起動時に変更できます。デバイス「${runner.name}」で、CPU・メモリはサイズの上限で動き、「GPU を使う」ではデバイスのすべての GPU を使います。`
							: "再起動は同じサイズを使い、次回の起動時に変更できます。 GPU サイズと「専有」と表示されたサイズは専有インスタンスをその場で起動するため、使えるようになるまで数分かかります (起動待ちの時間は課金されません)。"}
					</p>
				</div>
				<div
					style={{
						padding: "20px 24px",
						display: "flex",
						flexDirection: "column",
						gap: 8,
						maxHeight: "min(60vh, 560px)",
						overflowY: "auto",
					}}
				>
					{sizes === null &&
						loadError === null &&
						Array.from({ length: placeholderCount }, (_, i) => (
							<SizeOptionPlaceholder key={i} onRunner={runner !== null} />
						))}
					{loadError && (
						<div
							style={{
								padding: "10px 14px",
								borderRadius: "var(--radius-3)",
								background: "var(--danger-subtle)",
								boxShadow: "0 0 0 1px var(--danger-line)",
								fontSize: "var(--text-ui)",
								color: "var(--danger)",
								fontFamily: "var(--font-sans)",
							}}
						>
							{loadError}
						</div>
					)}
					{sizes && (
						<>
							<SectionHeading>汎用</SectionHeading>
							{shared.map((info) => (
								<SizeOption
									key={info.size}
									info={info}
									checked={selected === info.size}
									onSelect={() => setSelected(info.size)}
									onRunner={runner !== null}
									disabledReason={sizeDisabledReason(info)}
								/>
							))}
							{runner && (
								<>
									<SectionHeading>GPU</SectionHeading>
									<GpuOption
										checked={gpuChecked}
										onChange={setGpuChecked}
										disabledReason={gpuDisabledReason}
									/>
								</>
							)}
							{gpu.length > 0 && (
								<>
									<SectionHeading>GPU (専有)</SectionHeading>
									{gpu.map((info) => (
										<SizeOption
											key={info.size}
											info={info}
											checked={selected === info.size}
											onSelect={() => setSelected(info.size)}
										/>
									))}
								</>
							)}
						</>
					)}
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						borderTop: "var(--pane-header-border)",
						padding: "12px 24px",
					}}
				>
					<ConfirmButton variant="ghost" label="キャンセル" loading={false} onClick={onClose} />
					<ConfirmButton
						type="submit"
						label="起動"
						loading={false}
						disabled={sizes === null || selectedUnavailable}
						data-testid="environment-instance-size-submit"
					/>
				</div>
			</form>
		</dialog>
	);
}

/** 節見出し (汎用 / GPU)。 */
function SectionHeading({ children }: { children: React.ReactNode }) {
	return (
		<div
			style={{
				fontSize: "var(--text-caption)",
				fontWeight: 600,
				color: "var(--fg-3)",
				fontFamily: "var(--font-sans)",
				padding: "6px 0 0",
			}}
		>
			{children}
		</div>
	);
}

/**
 * 読み込み中は選択できるサイズと同数の同寸プレースホルダーを先に描き、一覧が届いた瞬間に
 * ダイアログの高さが変わってちらつかないようにする。
 */
function SizeOptionPlaceholder({ onRunner }: { onRunner: boolean }) {
	return (
		<div
			data-testid="environment-instance-size-placeholder"
			style={{
				display: "flex",
				alignItems: "center",
				gap: 12,
				padding: "10px 12px",
				borderRadius: "var(--radius-3)",
				boxShadow: "var(--shadow-ring)",
			}}
		>
			<span
				style={{
					width: 13,
					height: 13,
					borderRadius: "var(--radius-full)",
					background: "var(--surface-2)",
				}}
			/>
			<span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
				<span style={{ fontSize: "var(--text-ui)", color: "transparent" }}>
					<span
						style={{
							display: "inline-block",
							width: 140,
							height: "0.8em",
							background: "var(--surface-2)",
							borderRadius: "var(--radius-1)",
						}}
					/>
				</span>
				<span style={{ fontSize: "var(--text-caption)", color: "transparent" }}>
					<span
						style={{
							display: "inline-block",
							width: 200,
							height: "0.8em",
							background: "var(--surface-2)",
							borderRadius: "var(--radius-1)",
						}}
					/>
				</span>
			</span>
			{!onRunner && (
				<span style={{ fontSize: "var(--text-ui)", color: "transparent" }}>
					<span
						style={{
							display: "inline-block",
							width: 120,
							height: "0.8em",
							background: "var(--surface-2)",
							borderRadius: "var(--radius-1)",
						}}
					/>
				</span>
			)}
		</div>
	);
}

/** デバイスの環境の「GPU を使う」。GPU を報告していないデバイスでは選べず、理由を出す。 */
function GpuOption({
	checked,
	onChange,
	disabledReason,
}: {
	checked: boolean;
	onChange: (checked: boolean) => void;
	disabledReason: string | null;
}) {
	const disabled = disabledReason !== null;
	return (
		<>
			{disabledReason && (
				<p
					data-testid="environment-instance-size-gpu-disabled-reason"
					style={{
						margin: 0,
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-body)",
						color: "var(--fg-2)",
						fontFamily: "var(--font-sans)",
					}}
				>
					{disabledReason}
				</p>
			)}
			<label
				data-testid="environment-instance-size-gpu-option"
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					padding: "10px 12px",
					borderRadius: "var(--radius-3)",
					background: checked ? "var(--row-selected-bg)" : "transparent",
					boxShadow: checked ? "0 0 0 1px var(--accent)" : "var(--shadow-ring)",
					cursor: disabled ? "not-allowed" : "pointer",
					opacity: disabled ? 0.5 : 1,
					fontFamily: "var(--font-sans)",
				}}
			>
				<input
					type="checkbox"
					checked={checked}
					disabled={disabled}
					onChange={(e) => onChange(e.target.checked)}
					data-testid="environment-instance-size-gpu"
				/>
				<span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
					<span style={{ fontSize: "var(--text-ui)", fontWeight: 600, color: "var(--fg-1)" }}>
						GPU を使う
					</span>
					<span style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
						デバイスのすべての GPU
					</span>
				</span>
			</label>
		</>
	);
}

/**
 * サイズ 1 件の選択行。ラベル・リソース (GPU サイズは GPU の行も)・時間あたりのクレジット (クラウドだけ) をまとめる。
 * 選べないサイズは薄くし、理由を行の中に出す。
 */
function SizeOption({
	info,
	checked,
	onSelect,
	onRunner = false,
	disabledReason = null,
}: {
	info: EnvironmentInstanceSizeInfo;
	checked: boolean;
	onSelect: () => void;
	/** デバイスの環境。稼働は課金しないので単価を出さない。 */
	onRunner?: boolean;
	disabledReason?: string | null;
}) {
	const disabled = disabledReason !== null;
	const dimmed = { opacity: disabled ? 0.5 : 1 };
	return (
		<label
			data-testid={`environment-instance-size-option-${info.size}`}
			style={{
				display: "flex",
				alignItems: "center",
				gap: 12,
				padding: "10px 12px",
				borderRadius: "var(--radius-3)",
				background: checked ? "var(--row-selected-bg)" : "transparent",
				boxShadow: checked ? "0 0 0 1px var(--accent)" : "var(--shadow-ring)",
				cursor: disabled ? "not-allowed" : "pointer",
				fontFamily: "var(--font-sans)",
			}}
		>
			<input
				type="radio"
				name="environment-instance-size"
				value={info.size}
				checked={checked}
				disabled={disabled}
				onChange={onSelect}
				style={dimmed}
				data-testid={`environment-instance-size-radio-${info.size}`}
			/>
			<span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
				<span
					style={{ ...dimmed, fontSize: "var(--text-ui)", fontWeight: 600, color: "var(--fg-1)" }}
				>
					{info.label}
				</span>
				<span style={{ ...dimmed, fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
					CPU {formatCpuRange(info.cpuRequest, info.cpuLimit)} / メモリ {formatMemory(info.memory)}
					{info.dedicated && !info.gpu && !onRunner && " / 専有 (使えるまで数分かかります)"}
				</span>
				{info.gpu && (
					<span style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}>
						GPU {info.gpu.name} ×{info.gpu.count} / GPU メモリ {info.gpu.totalMemoryGb} GB
					</span>
				)}
				{disabledReason && (
					<span
						data-testid={`environment-instance-size-disabled-reason-${info.size}`}
						style={{ fontSize: "var(--text-caption)", color: "var(--fg-2)" }}
					>
						{disabledReason}
					</span>
				)}
			</span>
			{!onRunner && !info.deviceOnly && (
				<span
					style={{
						flexShrink: 0,
						fontSize: "var(--text-ui)",
						fontWeight: 600,
						color: "var(--fg-1)",
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{formatCredits(info.creditsPerHour)}
					<span
						style={{
							fontSize: "var(--text-caption)",
							fontWeight: 500,
							color: "var(--fg-3)",
							marginLeft: 4,
						}}
					>
						クレジット / 時間
					</span>
				</span>
			)}
		</label>
	);
}
